/** Bursts de partículas reciclados (impacto de hit, morte, telegraph). Baseado em THREE.Points. */
import * as THREE from "three";
import { Pool } from "../utils/Pool";
import { quality } from "../core/Quality";

const PER_BURST = 20; // teto de partículas por burst; a contagem real escala com a qualidade

interface Burst {
  points: THREE.Points;
  vel: Float32Array;
  positions: Float32Array;
  attr: THREE.BufferAttribute;
  mat: THREE.PointsMaterial;
  life: number;
  dur: number;
  n: number; // partículas ativas neste burst (draw range)
}

export class ParticleSystem {
  private readonly active: Burst[] = [];
  private readonly pool: Pool<Burst>;

  constructor(private readonly scene: THREE.Scene) {
    this.pool = new Pool<Burst>(
      () => this.make(),
      (b) => {
        b.points.visible = false;
      },
      8,
    );
  }

  burst(x: number, y: number, z: number, color: number, opts?: { count?: number; speed?: number; dur?: number }): void {
    const b = this.pool.acquire();
    const speed = opts?.speed ?? 4;
    // contagem pedida, escalada pela qualidade (mobile reduz) e limitada ao teto do pool
    const want = Math.round((opts?.count ?? PER_BURST) * quality.values.particleScale);
    b.n = Math.max(1, Math.min(PER_BURST, want));
    b.mat.color.setHex(color);
    b.mat.opacity = 1;
    for (let i = 0; i < b.n; i++) {
      const o = i * 3;
      b.positions[o] = x;
      b.positions[o + 1] = y;
      b.positions[o + 2] = z;
      const a = Math.random() * Math.PI * 2;
      const up = 0.4 + Math.random() * 1.2;
      const r = (0.3 + Math.random()) * speed;
      b.vel[o] = Math.cos(a) * r;
      b.vel[o + 1] = up * speed * 0.6;
      b.vel[o + 2] = Math.sin(a) * r;
    }
    b.attr.needsUpdate = true;
    b.points.geometry.setDrawRange(0, b.n);
    b.points.visible = true;
    b.life = 0;
    b.dur = opts?.dur ?? 0.5;
    this.active.push(b);
  }

  update(dt: number): void {
    for (let i = this.active.length - 1; i >= 0; i--) {
      const b = this.active[i];
      b.life += dt;
      const t = b.life / b.dur;
      if (t >= 1) {
        this.pool.release(b);
        this.active.splice(i, 1);
        continue;
      }
      for (let p = 0; p < b.n; p++) {
        const o = p * 3;
        b.vel[o + 1] -= 6 * dt; // gravidade
        b.positions[o] += b.vel[o] * dt;
        b.positions[o + 1] += b.vel[o + 1] * dt;
        b.positions[o + 2] += b.vel[o + 2] * dt;
      }
      b.attr.needsUpdate = true;
      b.mat.opacity = 1 - t;
    }
  }

  clear(): void {
    for (const b of this.active) this.pool.release(b);
    this.active.length = 0;
  }

  private make(): Burst {
    const positions = new Float32Array(PER_BURST * 3);
    const geo = new THREE.BufferGeometry();
    const attr = new THREE.BufferAttribute(positions, 3);
    geo.setAttribute("position", attr);
    const mat = new THREE.PointsMaterial({
      size: 0.22,
      transparent: true,
      opacity: 1,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      sizeAttenuation: true,
    });
    const points = new THREE.Points(geo, mat);
    points.frustumCulled = false;
    points.visible = false;
    this.scene.add(points);
    return { points, vel: new Float32Array(PER_BURST * 3), positions, attr, mat, life: 0, dur: 0.5, n: PER_BURST };
  }
}
